import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { getApi } from '../../../Services/apiService';

const PetManagement = () => {
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [editingPetId, setEditingPetId] = useState(null);
    const [deletingPetId, setDeletingPetId] = useState(null);
    const [viewingPetId, setViewingPetId] = useState(null);
    const [viewPetData, setViewPetData] = useState(null);
    const [viewLoader, setViewLoader] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        type: '',
        breed: '',
        age: '',
        owner: '',
        status: '',
        photo: null,
        isActive: true
    });
    const [formErrors, setFormErrors] = useState({});
    const [loader, setLoader] = useState(false);
    const [photoPreview, setPhotoPreview] = useState(null);
    
    // API state
    const [pets, setPets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [statusFilter, setStatusFilter] = useState(''); // '', 'seen', 'lost', 'found'
    const [totalPets, setTotalPets] = useState(0);
    const [lostPetsCount, setLostPetsCount] = useState(0);
    const [stillMissingCount, setStillMissingCount] = useState(0);
    const [resolvedCount, setResolvedCount] = useState(0);
    const [pagination, setPagination] = useState({
        page: 1,
        take: 10,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });

    const handleView = (id) => {
        navigate(`/admin/pet-management/${id}`);
    };

    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setViewingPetId(null);
        setViewPetData(null);
    };

    const handleEdit = (id) => {
        const pet = pets.find(p => p.id === id);
        if (pet) {
            setEditingPetId(id);
            setFormData({
                name: pet.name || '',
                type: pet.type || '',
                breed: pet.breed || '',
                age: pet.age || '',
                owner: pet.owner || '',
                status: pet.status || '',
                photo: null,
                isActive: pet.isActive !== undefined ? pet.isActive : true
            });
            setPhotoPreview(pet.photo || null);
            setFormErrors({});
            setIsEditModalOpen(true);
        }
    };

    const handleDelete = (id) => {
        setDeletingPetId(id);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setDeletingPetId(null);
    };

    const confirmDelete = async () => {
        if (!deletingPetId) return;
        
        setDeleteLoader(true);
        try {
            // TODO: Implement delete pet API call
            toast.info('Delete functionality coming soon');
            closeDeleteModal();
        } catch (error) {
            console.error('Error deleting pet:', error);
            toast.error('Failed to delete pet. Please try again.');
        } finally {
            setDeleteLoader(false);
        }
    };

    // Fetch pets from API: /api/v1/admin/pets?page=1&limit=10&status=lost
    // Response: { data: { counts, pets, pagination?, total } }
    const fetchPets = async () => {
        setLoading(true);
        try {
            const statusParam = statusFilter ? `&status=${encodeURIComponent(statusFilter)}` : '';
            const response = await getApi(`/api/v1/admin/pets?page=${page}&limit=${limit}${statusParam}`);
            const data = response?.data ?? response ?? {};
            const listArray = Array.isArray(data.pets) ? data.pets : [];
            const counts = data.counts ?? {};
            const total = data.total ?? listArray.length;
            setPets(listArray);
            setTotalPets(total);
            setLostPetsCount(counts.lostPetCount ?? 0);
            setStillMissingCount(counts.stillMissingCount ?? 0);
            setResolvedCount(counts.foundPetCount ?? 0);

            const pag = data.pagination;
            if (pag) {
                setPagination({
                    page: pag.page ?? page,
                    take: pag.take ?? pag.limit ?? limit,
                    totalItems: pag.totalItems ?? total,
                    totalPages: pag.totalPages ?? (Math.ceil(total / limit) || 1),
                    hasNextPage: Boolean(pag.hasNextPage),
                    hasPreviousPage: Boolean(pag.hasPreviousPage)
                });
            } else {
                const totalPages = Math.ceil(total / limit) || 1;
                setPagination({
                    page,
                    take: limit,
                    totalItems: total,
                    totalPages,
                    hasNextPage: page < totalPages,
                    hasPreviousPage: page > 1
                });
            }
        } catch (error) {
            console.error('Error fetching pets:', error);
            setPets([]);
            setTotalPets(0);
            setPagination({ page: 1, take: limit, totalItems: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, statusFilter]);

    const handleAddPet = () => {
        setIsModalOpen(true);
    };

    const handleExport = () => {
        toast.info('Export functionality coming soon');
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsEditModalOpen(false);
        setEditingPetId(null);
        setFormData({
            name: '',
            type: '',
            breed: '',
            age: '',
            owner: '',
            status: '',
            photo: null,
            isActive: true
        });
        setFormErrors({});
        setPhotoPreview(null);
    };

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        
        if (name === 'photo' && files && files[0]) {
            const file = files[0];
            setFormData(prev => ({
                ...prev,
                [name]: file
            }));
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.name.trim()) {
            errors.name = 'Pet name is required';
        }
        
        if (!formData.type.trim()) {
            errors.type = 'Pet type is required';
        }
        
        if (!formData.breed.trim()) {
            errors.breed = 'Breed is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoader(true);
        try {
            // TODO: Implement add pet API call
            toast.info('Add pet functionality coming soon');
            closeModal();
            fetchPets();
        } catch (error) {
            console.error('Error adding pet:', error);
            toast.error('Failed to add pet. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoader(true);
        try {
            // TODO: Implement update pet API call
            toast.info('Update pet functionality coming soon');
            closeModal();
            fetchPets();
        } catch (error) {
            console.error('Error updating pet:', error);
            toast.error('Failed to update pet. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    const getStatusPillClass = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'seen') return 'seen';
        if (s === 'lost') return 'lost';
        if (s === 'found') return 'found';
        return 'inactive';
    };

    const displayStatus = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'seen') return 'Seen';
        if (s === 'lost') return 'Lost';
        if (s === 'found') return 'Found';
        return status || '-';
    };

    return (
        <div className='RightSideDiv'>
            {/* Statistics Cards - Screenshot 1: Lost pets, Still Missing, Resolved/found pet */}
            <div className="dashboard-stats dashboard-stats-3">
                <div className="stat-card">
                    <h2 className="stat-number">{lostPetsCount}</h2>
                    <h3 className="stat-label">Lost pets</h3>
                </div>
                <div className="stat-card">
                    <h2 className="stat-number">{stillMissingCount}</h2>
                    <h3 className="stat-label">Still Missing</h3>
                </div>
                <div className="stat-card">
                    <h2 className="stat-number">{resolvedCount}</h2>
                    <h3 className="stat-label">Resolved/found pet</h3>
                </div>
            </div>

            {/* Filter + Table - Screenshot 1 layout */}
            <div className="pet-management-filter-row">
                <span className="pet-management-filter-label">Filter</span>
                <select
                    className="users-filter-select pet-management-filter-select"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    aria-label="Filter by status"
                >
                    <option value="">All</option>
                    <option value="seen">Seen</option>
                    <option value="lost">Lost</option>
                    <option value="found">found</option>
                </select>
            </div>

            <div className="shelters-section">
                <div className="table-outer">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p>Loading pets...</p>
                        </div>
                    ) : pets.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p>No pets found.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="table pet-management-table">
                                <thead>
                                    <tr className="t-heading">
                                        <th><h5>Sr.no.</h5></th>
                                        <th><h5>Pet Name</h5></th>
                                        <th><h5>Breed</h5></th>
                                        <th><h5>Status</h5></th>
                                        <th><h5>Age</h5></th>
                                        <th><h5>Badge</h5></th>
                                        <th><h5>Action</h5></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pets.map((pet, index) => (
                                        <tr key={pet.id || index}>
                                            <td><h5>{pet.srNo ?? (page - 1) * limit + index + 1}</h5></td>
                                            <td><h5>{pet.petName ?? pet.name ?? '-'}</h5></td>
                                            <td><h5>{pet.breed ?? '-'}</h5></td>
                                            <td>
                                                <span className={`status-badge status-badge-pet ${getStatusPillClass(pet.status)}`}>
                                                    {displayStatus(pet.status)}
                                                </span>
                                            </td>
                                            <td><h5>{pet.age ?? '-'}</h5></td>
                                            <td>
                                                {pet.badge ? (
                                                    <span className="pet-badge-safety">
                                                        <span className="pet-badge-safety-icon" aria-hidden><img src={window.getPublicUrl('/assets/images/safty.svg')} alt="Shield" /></span>
                                                        {pet.badge}
                                                    </span>
                                                ) : (
                                                    <h5>-</h5>
                                                )}
                                            </td>
                                            <td className="action">
                                                <button
                                                    className="view-detail-btn"
                                                    onClick={() => handleView(pet.id)}
                                                    title="View Detail"
                                                >
                                                    View Detail
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {!loading && pagination.totalPages > 0 && (
                    <div className="pagination-bar">
                        <div className="pagination-controls">
                            <button type="button" className="pagination-btn pagination-prev-next" disabled={!pagination.hasPreviousPage} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <div className="pagination-pages">
                                {(() => {
                                    const { page: currentPage, totalPages } = pagination;
                                    const pages = [];
                                    const showEllipsisStart = totalPages > 5 && currentPage > 3;
                                    const showEllipsisEnd = totalPages > 5 && currentPage < totalPages - 2;
                                    let start = 1, end = totalPages;
                                    if (totalPages > 5) {
                                        if (currentPage <= 3) { start = 1; end = 4; } else if (currentPage >= totalPages - 2) { start = totalPages - 3; end = totalPages; } else { start = currentPage - 1; end = currentPage + 1; }
                                    }
                                    if (showEllipsisStart) pages.push(1, 'ellipsis-start');
                                    for (let i = start; i <= end; i++) pages.push(i);
                                    if (showEllipsisEnd && end < totalPages) pages.push('ellipsis-end', totalPages);
                                    return pages.map((item, idx) =>
                                        item === 'ellipsis-start' || item === 'ellipsis-end' ? <span key={`e-${idx}`} className="pagination-ellipsis">…</span> : (
                                            <button key={item} type="button" className={`pagination-btn pagination-page ${item === currentPage ? 'active' : ''}`} onClick={() => setPage(item)} aria-label={`Page ${item}`} aria-current={item === currentPage ? 'page' : undefined}>{item}</button>
                                        )
                                    );
                                })()}
                            </div>
                            <button type="button" className="pagination-btn pagination-prev-next" disabled={!pagination.hasNextPage} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Add Pet Modal */}
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel="Add Pet Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>Add Pet</h2>
                        <p>Pet basic information</p>
                    </div>
                    <button className="close-btn" onClick={closeModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-content">
                        <div className="form-group">
                            <label htmlFor="name">Pet name <span className="required">*</span></label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter pet name"
                                className={formErrors.name ? 'error' : ''}
                            />
                            {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="type">Type <span className="required">*</span></label>
                                <select
                                    id="type"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    className={formErrors.type ? 'error' : ''}
                                >
                                    <option value="">Select type</option>
                                    <option value="Dog">Dog</option>
                                    <option value="Cat">Cat</option>
                                    <option value="Bird">Bird</option>
                                    <option value="Other">Other</option>
                                </select>
                                {formErrors.type && <span className="error-message">{formErrors.type}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="breed">Breed <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="breed"
                                    name="breed"
                                    value={formData.breed}
                                    onChange={handleInputChange}
                                    placeholder="Enter breed"
                                    className={formErrors.breed ? 'error' : ''}
                                />
                                {formErrors.breed && <span className="error-message">{formErrors.breed}</span>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="age">Age</label>
                                <input
                                    type="text"
                                    id="age"
                                    name="age"
                                    value={formData.age}
                                    onChange={handleInputChange}
                                    placeholder="Enter age"
                                    className={formErrors.age ? 'error' : ''}
                                />
                                {formErrors.age && <span className="error-message">{formErrors.age}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="status">Status</label>
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    className={formErrors.status ? 'error' : ''}
                                >
                                    <option value="">Select status</option>
                                    <option value="Active">Active</option>
                                    <option value="Lost">Lost</option>
                                    <option value="Found">Found</option>
                                </select>
                                {formErrors.status && <span className="error-message">{formErrors.status}</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="owner">Owner</label>
                            <input
                                type="text"
                                id="owner"
                                name="owner"
                                value={formData.owner}
                                onChange={handleInputChange}
                                placeholder="Enter owner name"
                                className={formErrors.owner ? 'error' : ''}
                            />
                            {formErrors.owner && <span className="error-message">{formErrors.owner}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="photo">Photo</label>
                            <input
                                type="file"
                                id="photo"
                                name="photo"
                                accept="image/*"
                                onChange={handleInputChange}
                                className={formErrors.photo ? 'error' : ''}
                            />
                            {photoPreview && (
                                <div className="logo-preview">
                                    <img src={photoPreview} alt="Pet preview" />
                                </div>
                            )}
                            {formErrors.photo && <span className="error-message">{formErrors.photo}</span>}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="submit" className="submit-btn" disabled={loader}>
                            {loader ? 'Adding...' : 'Add Pet'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Edit Pet Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onRequestClose={closeModal}
                contentLabel="Edit Pet Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>Edit Pet</h2>
                        <p>Pet basic information</p>
                    </div>
                    <button className="close-btn" onClick={closeModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleUpdateSubmit}>
                    <div className="form-content">
                        <div className="form-group">
                            <label htmlFor="edit-name">Pet name <span className="required">*</span></label>
                            <input
                                type="text"
                                id="edit-name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter pet name"
                                className={formErrors.name ? 'error' : ''}
                            />
                            {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="edit-type">Type <span className="required">*</span></label>
                                <select
                                    id="edit-type"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    className={formErrors.type ? 'error' : ''}
                                >
                                    <option value="">Select type</option>
                                    <option value="Dog">Dog</option>
                                    <option value="Cat">Cat</option>
                                    <option value="Bird">Bird</option>
                                    <option value="Other">Other</option>
                                </select>
                                {formErrors.type && <span className="error-message">{formErrors.type}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="edit-breed">Breed <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="edit-breed"
                                    name="breed"
                                    value={formData.breed}
                                    onChange={handleInputChange}
                                    placeholder="Enter breed"
                                    className={formErrors.breed ? 'error' : ''}
                                />
                                {formErrors.breed && <span className="error-message">{formErrors.breed}</span>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="edit-age">Age</label>
                                <input
                                    type="text"
                                    id="edit-age"
                                    name="age"
                                    value={formData.age}
                                    onChange={handleInputChange}
                                    placeholder="Enter age"
                                    className={formErrors.age ? 'error' : ''}
                                />
                                {formErrors.age && <span className="error-message">{formErrors.age}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="edit-status">Status</label>
                                <select
                                    id="edit-status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    className={formErrors.status ? 'error' : ''}
                                >
                                    <option value="">Select status</option>
                                    <option value="Active">Active</option>
                                    <option value="Lost">Lost</option>
                                    <option value="Found">Found</option>
                                </select>
                                {formErrors.status && <span className="error-message">{formErrors.status}</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-owner">Owner</label>
                            <input
                                type="text"
                                id="edit-owner"
                                name="owner"
                                value={formData.owner}
                                onChange={handleInputChange}
                                placeholder="Enter owner name"
                                className={formErrors.owner ? 'error' : ''}
                            />
                            {formErrors.owner && <span className="error-message">{formErrors.owner}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-photo">Photo</label>
                            <input
                                type="file"
                                id="edit-photo"
                                name="photo"
                                accept="image/*"
                                onChange={handleInputChange}
                                className={formErrors.photo ? 'error' : ''}
                            />
                            {photoPreview && (
                                <div className="logo-preview">
                                    <img src={photoPreview} alt="Pet preview" />
                                </div>
                            )}
                            {formErrors.photo && <span className="error-message">{formErrors.photo}</span>}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="submit" className="submit-btn" disabled={loader}>
                            {loader ? 'Updating...' : 'Update Pet'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onRequestClose={closeDeleteModal}
                contentLabel="Delete Confirmation Modal"
                className="delete-confirmation-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="delete-modal-header">
                    <button className="close-btn" onClick={closeDeleteModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <div className="delete-modal-content">
                    <div className="delete-warning-icon">
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="32" cy="32" r="32" fill="#f44336"/>
                            <path d="M32 20V36" stroke="white" strokeWidth="4" strokeLinecap="round"/>
                            <circle cx="32" cy="44" r="2" fill="white"/>
                        </svg>
                    </div>
                    
                    <h2 className="delete-modal-title">Delete Confirmation</h2>
                    
                    <p className="delete-modal-message">
                        Are you sure you want to delete this pet
                    </p>
                    
                    <div className="delete-modal-actions">
                        <button 
                            className="delete-confirm-btn" 
                            onClick={confirmDelete}
                            disabled={deleteLoader}
                        >
                            {deleteLoader ? 'Deleting...' : 'Delete'}
                        </button>
                        <button 
                            className="delete-cancel-btn" 
                            onClick={closeDeleteModal}
                            disabled={deleteLoader}
                        >
                            Not now
                        </button>
                    </div>
                </div>
            </Modal>

            {/* View Pet Modal */}
            <Modal
                isOpen={isViewModalOpen}
                onRequestClose={closeViewModal}
                contentLabel="View Pet Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>View Pet</h2>
                        <p>Pet basic information</p>
                    </div>
                    <button className="close-btn" onClick={closeViewModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                {viewLoader ? (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                        <p>Loading pet details...</p>
                    </div>
                ) : viewPetData ? (
                    <div className="form-content view-content">
                        <div className="form-group">
                            <label>Pet name</label>
                            <div className="view-field-value">{viewPetData.name || 'N/A'}</div>
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label>Type</label>
                                <div className="view-field-value">{viewPetData.type || 'N/A'}</div>
                            </div>

                            <div className="form-group form-group-half">
                                <label>Breed</label>
                                <div className="view-field-value">{viewPetData.breed || 'N/A'}</div>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label>Age</label>
                                <div className="view-field-value">{viewPetData.age || 'N/A'}</div>
                            </div>

                            <div className="form-group form-group-half">
                                <label>Status</label>
                                <div className="view-field-value">
                                    <span className={`status-badge ${viewPetData.status === 'Active' ? 'active' : 'inactive'}`}>
                                        {viewPetData.status || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Owner</label>
                            <div className="view-field-value">{viewPetData.owner || 'N/A'}</div>
                        </div>

                        {viewPetData.photo && (
                            <div className="form-group">
                                <label>Photo</label>
                                <div className="logo-preview">
                                    <img 
                                        src={viewPetData.photo.startsWith('http') ? viewPetData.photo : window.getPublicUrl(viewPetData.photo)} 
                                        alt="Pet photo"
                                        onError={(e) => {
                                            e.target.src = window.getPublicUrl('/assets/images/dummy-profile.jpg');
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                        <p>No data available</p>
                    </div>
                )}

                <div className="modal-footer">
                    <button className="submit-btn" onClick={closeViewModal}>
                        Close
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default PetManagement;
